import {
  CheckableFormElement,
  checkableFormElementStyles
} from "./chunk.DJPU2C2P.js";
import {
  emit
} from "./chunk.HM4ZRC4T.js";
import {
  focusVisibleSelector
} from "./chunk.ZX7YPA4S.js";
import {
  hasSlotWithSpecificElements
} from "./chunk.Z2CDDKXX.js";
import {
  watch
} from "./chunk.HBM5K2JV.js";
import {
  __decorateClass,
  component_styles_default,
  theme_styles_default
} from "./chunk.U4VUMZVF.js";

// src/components/radio-group/radio-group.ts
import { html as html2, LitElement } from "lit";
import { customElement as customElement2, property as property2, query, state as state2 } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

// src/components/radio/radio.ts
import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";

// src/components/radio/radio.styles.ts
import { css } from "lit";
var radio_styles_default = css`
	${theme_styles_default}
	${component_styles_default}
	${checkableFormElementStyles}

	.radio__icon {
		display: inline-flex;
		width: var(--yotpo-toggle-size);
		height: var(--yotpo-toggle-size);
	}

	.radio__icon svg {
		width: 100%;
		height: 100%;
	}

	.radio__control {
		flex: 0 0 auto;
		position: relative;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: var(--yotpo-toggle-size);
		height: var(--yotpo-toggle-size);
		border-width: var(--yotpo-input-border-width);
		border-style: var(--yotpo-input-border-style);
		border-color: var(--yotpo-color-brand-30);
		border-radius: 50%;
		background-color: var(--yotpo-input-background-color);
		color: transparent;
		transition: var(--yotpo-transition-fast) border-color, var(--yotpo-transition-fast) background-color,
			var(--yotpo-transition-fast) color, var(--yotpo-transition-fast) box-shadow;
	}

	.radio__input {
		position: absolute;
		opacity: 0;
		padding: 0;
		margin: 0;
		pointer-events: none;
	}

	/* Hover */
	.checkable_form_element:not(.checkable_form_element--checked):not(.checkable_form_element--disabled):hover
		.radio__control {
		color: var(--yotpo-color-brand-50);
		border-color: var(--yotpo-color-brand-50);
	}

	/* Focus */
	.checkable_form_element:not(.checkable_form_element--checked):not(.checkable_form_element--disabled)
		.radio__input${focusVisibleSelector}
		~ .radio__control {
		border-color: var(--yotpo-color-brand-50);
	}

	.checkable_form_element:not(.checkable_form_element--checked):not(.radio--selection-area):not(.checkable_form_element--disabled)
		.radio__input${focusVisibleSelector}
		~ .radio__control {
		box-shadow: var(--yotpo-focus-global);
	}

	/* Checked */
	.checkable_form_element--checked .radio__control {
		color: var(--yotpo-color-brand-70);
		border-color: var(--yotpo-color-brand-30);
	}

	/* Checked + hover */
	.checkable_form_element.checkable_form_element--checked:not(.checkable_form_element--disabled):hover
		.radio__control {
		border-color: var(--yotpo-color-brand-50);
		color: var(--yotpo-color-brand-60);
	}

	/* Checked + focus */
	.checkable_form_element.checkable_form_element--checked:not(.checkable_form_element--disabled)
		.radio__input${focusVisibleSelector}
		~ .radio__control {
		border-color: var(--yotpo-color-brand-50);
	}

	.checkable_form_element.checkable_form_element--checked:not(.radio--selection-area):not(.checkable_form_element--disabled)
		.radio__input${focusVisibleSelector}
		~ .radio__control {
		box-shadow: var(--yotpo-focus-global);
	}

	.checkable_form_element:not(.checkable_form_element--checked).checkable_form_element--disabled .radio__control {
		border-color: var(--yotpo-color-disabled);
	}

	/* Checked + Disabled */
	.checkable_form_element.checkable_form_element--checked.checkable_form_element--disabled .radio__control {
		border-color: var(--yotpo-color-disabled);
		color: var(--yotpo-color-disabled);
	}

	/* Disabled */
	.checkable_form_element--disabled {
		cursor: not-allowed;
	}

	/* When the control isn't checked, hide the circle for Windows High Contrast mode a11y */
	.checkable_form_element:not(.checkable_form_element--checked) svg circle {
		opacity: 0;
	}

	/* Selection Area */
	.checkable_form_element.radio--selection-area {
		border-width: var(--yotpo-input-border-width);
		border-style: var(--yotpo-input-border-style);
		border-color: var(--yotpo-color-brand-30);
		border-radius: var(--yotpo-border-radius-large);
		padding: var(--yotpo-spacing-large) var(--yotpo-spacing-x-large);
	}

	.checkable_form_element.checkable_form_element--checked:not(.checkable_form_element--disabled).radio--selection-area {
		border-color: var(--yotpo-color-brand-70);
	}

	.checkable_form_element.radio--selection-area:not(.checkable_form_element--disabled):has(.radio__input${focusVisibleSelector}) {
		box-shadow: var(--yotpo-focus-global);
	}

	.checkable_form_element.checkable_form_element--disabled.radio--selection-area {
		border-color: var(--yotpo-color-disabled);
	}
`;

// src/components/radio/radio.ts
var YOTPO_RADIO_TAG_NAME = "yotpo-radio";
var RADIO_SELECTION_AREA_CLASS = "radio--selection-area";
var id = 0;
var YotpoRadio = class extends CheckableFormElement {
  constructor() {
    super(...arguments);
    this._selectionArea = false;
  }
  set selectionArea(newValue) {
    this.setSelectionArea(newValue);
  }
  get selectionArea() {
    return this._selectionArea;
  }
  setSelectionArea(newValue) {
    this.updateComplete.then(() => {
      this._selectionArea = newValue;
      if (this._selectionArea) {
        this.base.classList.add(RADIO_SELECTION_AREA_CLASS);
      } else {
        this.base.classList.remove(RADIO_SELECTION_AREA_CLASS);
      }
    });
  }
  setChecked(newChecked, { emitEvent } = { emitEvent: true }) {
    this.setNewCheckedValue(newChecked);
    if (this.checked && emitEvent) {
      emit(this, "yotpo-change" /* change */, { detail: this.value });
    }
  }
  setRadioGroupStretch() {
    if (!this.radioGroup || !this.radioGroup.selectionArea) {
      return;
    }
    switch (this.radioGroup.direction) {
      case "vertical" /* vertical */:
        this.base.style.width = "100%";
        return;
      case "horizontal" /* horizontal */:
        this.base.style.height = "100%";
        return;
    }
  }
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("role", "radio");
  }
  firstUpdated() {
    super.firstUpdated();
    this.setInputLabel(`radio-label-${id}`, `radio-${++id}`);
  }
  get radioGroup() {
    return this.closest(YOTPO_RADIO_GROUP_TAG_NAME);
  }
  handleInputClick(event) {
    super.handleInputClick(event);
    if (this.checked) {
      return;
    }
    if (this.radioGroup) {
      this.radioGroup.setCheckedRadio(this);
    } else {
      this.input.tabIndex = 0;
      this.checked = true;
    }
  }
  render() {
    const radioInputAddition = html`
			<span part="control" class="radio__control">
				<span part="checked-icon" class="radio__icon">
					<svg viewBox="0 0 16 16">
						<g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
							<g fill="currentColor">
								<circle cx="8" cy="8" r="3.42857143"></circle>
							</g>
						</g>
					</svg>
				</span>
			</span>
		`;
    const radioData = {
      class: "radio__input",
      type: "radio",
      role: "radio",
      dataCy: "radio-input-el"
    };
    return this.renderCheckableFormElement(radioData, radioInputAddition);
  }
};
YotpoRadio.styles = radio_styles_default;
__decorateClass([
  state()
], YotpoRadio.prototype, "_selectionArea", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoRadio.prototype, "selectionArea", 1);
YotpoRadio = __decorateClass([
  customElement(YOTPO_RADIO_TAG_NAME)
], YotpoRadio);

// src/components/radio-group/radio-group.styles.ts
import { css as css2 } from "lit";
var radio_group_styles_default = css2`
	${theme_styles_default}
	${component_styles_default}

	::host {
		display: block;
	}

	.radio-group {
		display: flex;
	}

	.radio-group:not(.radio-group--has-radios) .radio-group__radios {
		display: none;
	}

	.radio-group--vertical {
		flex-direction: column;
	}

	.radio-group--horizontal {
		flex-direction: row;
	}

	.radio-group--vertical.radio-group--small ::slotted(yotpo-radio:not(:last-of-type)) {
		margin-bottom: var(--yotpo-spacing-medium);
	}

	.radio-group--vertical.radio-group--medium ::slotted(yotpo-radio:not(:last-of-type)) {
		margin-bottom: var(--yotpo-spacing-large);
	}

	.radio-group--vertical.radio-group--large ::slotted(yotpo-radio:not(:last-of-type)) {
		margin-bottom: var(--yotpo-spacing-x-large);
	}

	.radio-group--horizontal.radio-group--small ::slotted(yotpo-radio:not(:last-of-type)) {
		margin-right: var(--yotpo-spacing-medium);
	}

	.radio-group--horizontal.radio-group--medium ::slotted(yotpo-radio:not(:last-of-type)) {
		margin-right: var(--yotpo-spacing-large);
	}

	.radio-group--horizontal.radio-group--large ::slotted(yotpo-radio:not(:last-of-type)) {
		margin-right: var(--yotpo-spacing-x-large);
	}
`;

// src/components/radio-group/radio-group.ts
var YOTPO_RADIO_GROUP_TAG_NAME = "yotpo-radio-group";
var YotpoRadioGroup2 = class extends LitElement {
  constructor() {
    super(...arguments);
    this.initialValue = "";
    this.hasRadios = false;
    this.direction = "vertical" /* vertical */;
    this.selectionArea = false;
    this.labelSize = "small" /* small */;
    this._value = "";
  }
  get value() {
    return this._value;
  }
  set value(newValue) {
    if (!newValue || newValue === this.value) {
      return;
    }
    if (!this.value && !this.radios.length) {
      this.initialValue = newValue;
      return;
    }
    this.setValue(newValue);
  }
  setValue(newValue, { emitEvent } = { emitEvent: true }) {
    if (!this.radios.length) {
      return;
    }
    const radioOfValue = this.getRadioByValue(newValue) || this.radios[0];
    if (!radioOfValue) {
      return;
    }
    this._value = radioOfValue.value;
    this.setAttribute("value", this.value);
    this.setCheckedRadio(radioOfValue, emitEvent);
  }
  connectedCallback() {
    super.connectedCallback();
    this.handleSlotChange();
  }
  async firstUpdated() {
    await this.updateComplete;
    this.initializeRadios();
  }
  get radios() {
    return this.defaultSlot ? [...this.defaultSlot.assignedElements({ flatten: true })] : [];
  }
  get checkedRadio() {
    return this.radios.find((radio) => radio.checked);
  }
  getRadioByValue(value) {
    return this.radios.find((radio) => radio.value === value);
  }
  handleSlotChange() {
    this.hasRadios = hasSlotWithSpecificElements(this, [YOTPO_RADIO_TAG_NAME]);
  }
  handleFocusIn() {
    requestAnimationFrame(() => {
      const checkedRadio = this.radios.find((el) => el.checked) || this.radios[0];
      if (checkedRadio) {
        checkedRadio.focus();
      }
    });
  }
  handleKeyDown(event) {
    if (!this.radios || !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
      return;
    }
    event.preventDefault();
    const radios = this.radios.filter((radio) => !radio.disabled);
    const incr = ["ArrowUp", "ArrowLeft"].includes(event.key) ? -1 : 1;
    let index = (this.checkedRadio ? radios.indexOf(this.checkedRadio) : 0) + incr;
    if (index < 0) {
      index = radios.length - 1;
    }
    if (index > radios.length - 1) {
      index = 0;
    }
    this.value = radios[index].value;
    radios[index].focus();
  }
  handleRadioChange(event) {
    if (event.detail !== this.value) {
      this.setValue(event.detail, { emitEvent: false });
    }
  }
  handleSelectionAreaChange() {
    this.radios.forEach((radio) => {
      radio.selectionArea = this.selectionArea;
    });
  }
  handleLabelSizeChange() {
    this.radios.forEach((radio) => {
      radio.labelSize = this.labelSize;
    });
  }
  handleDirectionChange() {
    this.radios.forEach((radio) => {
      radio.setRadioGroupStretch();
    });
  }
  async setCheckedRadio(checkedRadio, emitEvent = true) {
    await this.updateComplete;
    if (checkedRadio === this.checkedRadio) {
      if (checkedRadio.input.tabIndex !== 0) {
        checkedRadio.input.tabIndex = 0;
      }
      this.radios.filter((radio) => radio !== checkedRadio).forEach((radio) => {
        radio.input.tabIndex = -1;
        radio.checked = false;
      });
      return;
    }
    this.radios.forEach((radio) => {
      const isRadioChecked = radio === checkedRadio;
      radio.input.tabIndex = isRadioChecked ? 0 : -1;
      radio.setChecked(isRadioChecked, { emitEvent: isRadioChecked && emitEvent });
    });
  }
  initializeRadios() {
    this.setRadiosPropertiesByRadioGroup();
    this.setInitialRadioGroupFocus();
    this.setInitialRadioGroupValue();
  }
  setRadiosPropertiesByRadioGroup() {
    this.radios.forEach((radio) => {
      radio.selectionArea = this.selectionArea;
      radio.labelSize = this.labelSize;
      radio.setRadioGroupStretch();
      if (radio.input) {
        radio.input.tabIndex = -1;
      }
    });
  }
  setInitialRadioGroupFocus() {
    if (this.checkedRadio && this.checkedRadio.input) {
      this.checkedRadio.input.tabIndex = 0;
    } else if (this.radios.length) {
      this.radios[0].input.tabIndex = 0;
    }
  }
  setInitialRadioGroupValue() {
    var _a;
    const initialRadioGroupValue = this.value || this.initialValue;
    const initialValue = initialRadioGroupValue || ((_a = this.checkedRadio) == null ? void 0 : _a.value);
    if (initialValue) {
      this.setValue(initialValue, { emitEvent: false });
    }
  }
  render() {
    return html2`
			<div
				part="base"
				class=${classMap({
      "radio-group": true,
      "radio-group--has-radios": this.hasRadios,
      "radio-group--vertical": this.direction === "vertical" /* vertical */,
      "radio-group--horizontal": this.direction === "horizontal" /* horizontal */,
      "radio-group--small": this.labelSize === "small" /* small */,
      "radio-group--medium": this.labelSize === "medium" /* medium */,
      "radio-group--large": this.labelSize === "large" /* large */
    })}
				role="radiogroup"
				@focusin=${this.handleFocusIn}
				@keydown=${this.handleKeyDown}
				@yotpo-change=${this.handleRadioChange}
			>
				<slot class="radio-group__radios" @slotchange=${this.handleSlotChange}></slot>
			</div>
		`;
  }
};
YotpoRadioGroup2.styles = radio_group_styles_default;
__decorateClass([
  query("slot:not([name])")
], YotpoRadioGroup2.prototype, "defaultSlot", 2);
__decorateClass([
  state2()
], YotpoRadioGroup2.prototype, "hasRadios", 2);
__decorateClass([
  property2({ reflect: true })
], YotpoRadioGroup2.prototype, "direction", 2);
__decorateClass([
  property2({ type: Boolean, reflect: true })
], YotpoRadioGroup2.prototype, "selectionArea", 2);
__decorateClass([
  property2({ reflect: true })
], YotpoRadioGroup2.prototype, "labelSize", 2);
__decorateClass([
  state2()
], YotpoRadioGroup2.prototype, "_value", 2);
__decorateClass([
  property2()
], YotpoRadioGroup2.prototype, "value", 1);
__decorateClass([
  watch("selectionArea", { waitUntilFirstUpdate: true })
], YotpoRadioGroup2.prototype, "handleSelectionAreaChange", 1);
__decorateClass([
  watch("labelSize", { waitUntilFirstUpdate: true })
], YotpoRadioGroup2.prototype, "handleLabelSizeChange", 1);
__decorateClass([
  watch("direction", { waitUntilFirstUpdate: true })
], YotpoRadioGroup2.prototype, "handleDirectionChange", 1);
YotpoRadioGroup2 = __decorateClass([
  customElement2(YOTPO_RADIO_GROUP_TAG_NAME)
], YotpoRadioGroup2);

export {
  YotpoRadioGroup2 as YotpoRadioGroup,
  YotpoRadio
};
